import { Reducer } from 'redux';
import {
  ACTION, RequestCases, ReceiveCases, RequestRemoveItem, ReceiveRemoveItem, FailRemoveItem,
  ReceivePermissions, ReceiveUiDefinitions, ClearResults, ReceiveRiskMatrixDefinitions, FailExecuteFavourite,
  RequestUiDefinitions, SetRenderMode, ClearInstance, AddInstance
} from './action-types';
import { CaseListingInstanceState, CaseListingStoreState } from './types-store';
import { RenderMode } from 'app/types-shared';

export const INITIAL_CASE_LISTING_INSTANCE_STATE: CaseListingInstanceState = {
  renderMode: RenderMode.report,
  fetching: false,
  requestId: 0,
  totalNumberOfResults: 0,
  entities: undefined,
  permissions: undefined,
  uiDefinitions: undefined,
  riskMatrixDefinitions: undefined,
  results: undefined,
  errorsExecuteFailed: undefined
};

export const INITIAL_CASE_LISTING_STATE: CaseListingStoreState = {
  instances: {},
  texts: {} as any
};

function transform(
  state: CaseListingStoreState, 
  instance: string, 
  requestId: number | undefined,
  transformation: (caseListing: CaseListingInstanceState) => CaseListingInstanceState): CaseListingStoreState {
  return {
    ...state,
    instances: Object.keys(state.instances).reduce((aggr, key) => {
      if (key === instance && (!requestId || state.instances[key].requestId === requestId)) {
        aggr[key] = transformation(state.instances[key]);
      }
      else {
        aggr[key] = state.instances[key];
      }
      return aggr;
    }, {})
  };
}

const reducers: { [indexer: string]: Reducer<CaseListingStoreState> } = {

  [ACTION.CLEAR_RESULT]: (state: CaseListingStoreState, action: ClearResults): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        ...INITIAL_CASE_LISTING_INSTANCE_STATE,
        caseListingInstanceKey: action.instanceKey,
        requestId: i.requestId + 1,
        renderMode: i.renderMode
      })
    );
  },

  [ACTION.RECEIVE_PERMISSIONS]: (state: CaseListingStoreState, action: ReceivePermissions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => {
        return {
          ...i,
          permissions: {
            ...(i.permissions ?? {}),
            ...action.permissions
          }
        };
      }
    );
  },
  [ACTION.REQUEST_UIDEFINITIONS]: (state: CaseListingStoreState, action: RequestUiDefinitions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        uiDefinitions: [
          ...(i.uiDefinitions ?? []).filter(
            ui => !action.uiDefinitions.some(a => ui.caseTypeId === a.caseTypeId && ui.caseContextId === a.caseContextId)
          ),
          ...action.uiDefinitions.map(ui => ({
            ...ui,
            fetching: true
          }))
        ]
      })
    );
  },
  [ACTION.RECEIVE_UIDEFINITIONS]: (state: CaseListingStoreState, action: ReceiveUiDefinitions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        uiDefinitions: (i.uiDefinitions ?? []).map(existingDefinition => {
          const receivedDefinition = action.uiDefinitions.find(
            u => existingDefinition.caseTypeId === u.caseTypeId && existingDefinition.caseContextId === u.caseContextId
          );
          return receivedDefinition ? 
            { ...receivedDefinition, fetching: false } : 
            existingDefinition;
        })
      })
    );
  },

  [ACTION.RECEIVE_RISKMATRIXDEFINITIONS]: (state: CaseListingStoreState, action: ReceiveRiskMatrixDefinitions): CaseListingStoreState => {
    
    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        riskMatrixDefinitions: {
          ...(i.riskMatrixDefinitions ?? {}),
          ...action.riskMatrixDefinitions
        }
      })
    );
  },

  /**
   * Run when starting fetching data for a case report favourite.
   */
  [ACTION.REQUEST_CASES]: (state: CaseListingStoreState, action: RequestCases): CaseListingStoreState => {
    
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        fetching: true,
        requestId: i.requestId + 1
      })
    );
  },

  /**
   * Run when finished fetching data for case report favourite.
   */
  [ACTION.RECEIVE_CASES]: (state: CaseListingStoreState, action: ReceiveCases): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        entities: {
          ...(i.entities || {}),
          ...action.entities
        },
        results: i.results ? i.results.concat(action.caseResults) : [...action.caseResults],
        fetching: false,
        totalNumberOfResults: action.totalNumberOfResults,
        errorsExecuteFailed: undefined
      })
    );
  },
  
  [ACTION.REPLACE_CASES]: (state: CaseListingStoreState, action: ReceiveCases): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        entities: action.entities,
        results:  action.caseResults,
        fetching: false,
        totalNumberOfResults: action.totalNumberOfResults,
        errorsExecuteFailed: undefined
      })
    );
  },

  [ACTION.REQUEST_REMOVE_ITEM]: (state: CaseListingStoreState, action: RequestRemoveItem): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        results: i.results.map(item => item.caseNumber !== action.itemId ? item : {
          ...item,
          deleted: true
        }),
        totalNumberOfResults: i.totalNumberOfResults - 1
      })
    );
  },

  [ACTION.RECEIVE_REMOVE_ITEM]: (state: CaseListingStoreState, action: ReceiveRemoveItem): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        results: i.results.filter(item => !item.deleted || item.caseNumber !== action.itemId)
      })
    );
  },

  [ACTION.FAIL_REMOVE_ITEM]: (state: CaseListingStoreState, action: FailRemoveItem): CaseListingStoreState => {

    return transform(
      state,
      action.instance,
      undefined,
      i => ({
        ...i,
        results: i.results.map(item => item.caseNumber !== action.itemId ? item : {
          ...item,
          deleted: false
        }),
        totalNumberOfResults: i.totalNumberOfResults + 1
      })
    );
  },

  [ACTION.FAIL_EXECUTE_FAVOURITE]: (state: CaseListingStoreState, action: FailExecuteFavourite): CaseListingStoreState => {
    
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        errorsExecuteFailed: action.errorsExecuteFailed
      })
    );
  },

  [ACTION.SET_RENDERMODE]: (state: CaseListingStoreState, action: SetRenderMode): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        renderMode: action.renderMode
      })
    );
  },

  [ACTION.ADD_INSTANCE]: (state: CaseListingStoreState, action: AddInstance): CaseListingStoreState => {
    return {
      ...state,
      instances: {
        ...state.instances,
        [action.instanceKey]: {
          ...INITIAL_CASE_LISTING_INSTANCE_STATE
        }
      }
    };
  },

  [ACTION.CLEAR_INSTANCE]: (state: CaseListingStoreState, action: ClearInstance): CaseListingStoreState => {

    return {
      ...state,
      instances: Object.keys(state?.instances).reduce(
        (aggr, key) => {
          if (key !== action.instanceKey) {
            aggr[key] = state.instances[key];
          }
          return aggr;
        }, {}
      )
    };
  }
};

export const reducer: Reducer<CaseListingStoreState> = (state: CaseListingStoreState = INITIAL_CASE_LISTING_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
